import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Save, Star, StarOff, DollarSign, Loader2, HelpCircle, Calculator } from "lucide-react";
import PageTour from "@/components/PageTour";
import LaborCategoriesPanel from "@/components/LaborCategoriesPanel";

const BLANK = { name: "", direct_rate: 75, fringe_pct: 0.32, overhead_pct: 0.55, ga_pct: 0.10, fee_pct: 0.08, is_default: false, notes: "" };

const previewMultiplier = (f) =>
  (1 + Number(f.fringe_pct || 0)) * (1 + Number(f.overhead_pct || 0)) * (1 + Number(f.ga_pct || 0)) - 1;

const fmt = (v, decimals = 0) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: decimals });

export default function WrapRates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rate_id being edited, or "new"
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/wrap-rates"); setRows(r.data || []); }
    catch { toast.error("Could not load wrap rates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row) => { setEditing(row.rate_id); setForm({ ...BLANK, ...row }); };
  const startNew = () => { setEditing("new"); setForm(BLANK); };
  const cancel = () => { setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/wrap-rates", form);
      else await api.put(`/wrap-rates/${editing}`, form);
      toast.success("Wrap rate saved");
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete wrap rate "${row.name}"?`)) return;
    try { await api.delete(`/wrap-rates/${row.rate_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const mult = previewMultiplier(form);
  const costPerHour = Number(form.direct_rate || 0) * (1 + mult);
  const pricePerHour = costPerHour * (1 + Number(form.fee_pct || 0));

  return (
    <div className="p-8 stagger-in max-w-6xl" data-testid="wrap-rates-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="bf-eyebrow inline-block text-violet-600 mb-3">COMPANY · WRAP RATES</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">
            Your <span className="bf-grad-text">FAR wrap rates</span>.
          </h1>
          <p className="text-slate-500 mt-3 max-w-2xl">Direct rate + fringe + overhead + G&amp;A = cost to government. Fee is kept separate per FAR cost-accounting rules. Applied to every services-mode estimate you generate.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <PageTour
            storageKey="bf_wraprates_tour_v1"
            eyebrow="WRAP RATES"
            title="How wrap rates work"
            Icon={Layers}
            tone="violet"
            steps={[
              { Icon: DollarSign, tone: "violet",  title: "1. Direct rate",     body: "The raw hourly labor cost you pay a labor category (before any burden). A senior engineer might be $85/hr direct; an admin $40/hr direct." },
              { Icon: Layers,     tone: "indigo",  title: "2. Fringe · overhead · G&A", body: "Fringe covers taxes, insurance, PTO. Overhead is your operating cost pool. G&A is corporate general/admin. Each is a % applied on top of the previous — compounded, per FAR." },
              { Icon: Calculator, tone: "amber",   title: "3. Wrap multiplier", body: "(1+fringe)(1+overhead)(1+G&A) − 1. A typical services shop lands around 1.20–1.60 (120-160% wrap on top of direct)." },
              { Icon: Star,       tone: "emerald", title: "4. Fee (kept separate)", body: "Profit target as a percent of cost-to-government. FAR keeps this outside the cost bucket, so we track it here as a separate field the pricing lead controls." },
              { Icon: Save,       tone: "fuchsia", title: "5. Mark one default",body: "Every services-mode estimate auto-picks your DEFAULT wrap rate on generation. Star a row to make it the default. Others stay in the library for special contracts (SCA, prevailing wage)." },
            ]}
          />
          <button onClick={startNew} className="bf-pill px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="wrap-add-btn">
            <Plus className="w-4 h-4" />NEW RATE
          </button>
        </div>
      </div>

      {editing && (
        <div className="mb-6 rounded-2xl bg-white border border-violet-200 shadow-sm p-6" data-testid="wrap-form">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] font-mono font-bold tracking-widest text-violet-700">{editing === "new" ? "NEW WRAP RATE" : "EDIT WRAP RATE"}</div>
            <button onClick={cancel} className="text-xs text-slate-500 hover:text-slate-800" data-testid="wrap-form-cancel">CANCEL</button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">NAME *</div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Services · FY26" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="wrap-name" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">DEFAULT DIRECT RATE ($/hr)</div>
              <input type="number" step="0.01" value={form.direct_rate} onChange={(e) => setForm({ ...form, direct_rate: parseFloat(e.target.value) || 0 })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none font-mono" data-testid="wrap-direct" />
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">EFFECTIVE FROM</div>
              <input type="date" value={form.effective_from || ""} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="wrap-effective" />
            </div>
            {[
              ["fringe_pct",   "FRINGE % (taxes, PTO, insurance)"],
              ["overhead_pct", "OVERHEAD %"],
              ["ga_pct",       "G&A %"],
              ["fee_pct",      "FEE % (profit target)"],
            ].map(([key, label]) => (
              <div key={key}>
                <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">{label}</div>
                <div className="relative">
                  <input type="number" step="0.001" value={form[key]} onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) || 0 })} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 pr-14 text-sm outline-none font-mono" data-testid={`wrap-${key}`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-400">= {(form[key] * 100).toFixed(1)}%</span>
                </div>
              </div>
            ))}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="w-4 h-4" data-testid="wrap-is-default" />
                <span className="text-sm text-slate-700">Set as workspace default</span>
              </label>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">NOTES</div>
              <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="What contracts is this rate for?" className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-3 py-2 text-sm outline-none" data-testid="wrap-notes" />
            </div>
          </div>

          <div className="mt-5 rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 p-4">
            <div className="text-[10px] font-mono font-bold tracking-widest text-violet-700 mb-3">LIVE PREVIEW · ${fmt(form.direct_rate, 2)}/hr direct</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[9px] font-mono font-bold tracking-widest text-slate-500">WRAP MULTIPLIER</div>
                <div className="font-display font-black text-xl text-violet-700 font-mono" data-testid="wrap-preview-multiplier">{(mult * 100).toFixed(1)}%</div>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[9px] font-mono font-bold tracking-widest text-slate-500">COST TO GOV / HR</div>
                <div className="font-display font-black text-xl text-slate-900 font-mono" data-testid="wrap-preview-cost">${fmt(costPerHour, 2)}</div>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[9px] font-mono font-bold tracking-widest text-slate-500">PRICE / HR (w/ FEE)</div>
                <div className="font-display font-black text-xl text-emerald-700 font-mono" data-testid="wrap-preview-price">${fmt(pricePerHour, 2)}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={cancel} className="bf-pill-ghost px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold" data-testid="wrap-form-cancel-btn">CANCEL</button>
            <button onClick={save} disabled={saving} className="bf-pill px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="wrap-form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}SAVE
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center" data-testid="wrap-empty">
          <Layers className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <div className="font-display font-bold text-lg text-slate-800 mb-2">No wrap rates yet</div>
          <div className="text-sm text-slate-500 mb-4">Add your first wrap rate so services-mode estimates auto-compute cost-to-government.</div>
          <button onClick={startNew} className="bf-pill px-5 py-2.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />ADD YOUR FIRST RATE
          </button>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono font-bold tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">NAME</th>
                <th className="px-4 py-3 text-right">DIRECT</th>
                <th className="px-4 py-3 text-right">FRINGE</th>
                <th className="px-4 py-3 text-right">OVERHEAD</th>
                <th className="px-4 py-3 text-right">G&amp;A</th>
                <th className="px-4 py-3 text-right">WRAP MULT</th>
                <th className="px-4 py-3 text-right">FEE</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.rate_id} className="hover:bg-violet-50/30" data-testid={`wrap-row-${r.rate_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.is_default && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />}
                      <span className="font-semibold text-slate-900">{r.name}</span>
                      {r.is_default && <span className="text-[9px] font-mono font-bold tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">DEFAULT</span>}
                    </div>
                    {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">${fmt(r.direct_rate, 2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">{(r.fringe_pct * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">{(r.overhead_pct * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">{(r.ga_pct * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-violet-700">{(r.wrap_multiplier * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{(r.fee_pct * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(r)} className="text-xs text-violet-600 hover:text-violet-800 font-semibold mr-3" data-testid={`wrap-edit-${r.rate_id}`}>EDIT</button>
                    <button onClick={() => remove(r)} className="text-slate-300 hover:text-rose-600" data-testid={`wrap-remove-${r.rate_id}`} aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LaborCategoriesPanel />
    </div>
  );
}
